import React from 'react';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Input,
} from '@material-ui/core';
import DayjsUtils from '@date-io/dayjs';
import { useFormik, getIn } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { getJustificationOptions } from 'services/justification';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { IGridModalCancelProps, IActivity, IOption } from './types';
import useStyles from './style';

const COMMENT_MAX = 3000;
const FORM_ID = 'CANCEL_FORM_MODAL';

const GridModalCancel = ({
  children,
  open,
  onClose,
  selectedRows,
  reloadData,
  cancelService,
}: IGridModalCancelProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [justificationOptions, setJustificationOptions] = React.useState<
    IOption[]
  >([]);

  const validationSchema = Yup.object({
    activities: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().nullable(),
          justification: Yup.string().required('Obrigatório'),
          comment: Yup.string().nullable().max(COMMENT_MAX, 'limite excedido'),
        }),
      )
      .min(selectedRows.length)
      .required(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activities: selectedRows.map(row => {
        return {
          activityRealId: row?.activityRealId,
          activityPlanId: row?.activityPlanId,
          comment: '',
        };
      }) as IActivity[],
    },
    validationSchema,
    onSubmit: formValues => {
      const params = formValues.activities.map(value => {
        const { comment, justification, activityRealId, activityPlanId } =
          value;
        return {
          comment,
          justificationId: justification,
          plannedActivityId: activityPlanId,
          performedActivityId: activityRealId,
        };
      });

      // se for enviado com sucesso
      setLoadingSaveForm(true);
      cancelService(params)
        .then(() => {
          // solicita um reload de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const queryJustificationOptions = useQuery({
    queryKey: ['JustificationOptionsContext'],
    queryFn: async () => {
      const { data } = await getJustificationOptions();
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setJustificationOptions(data);
    },
    enabled: true,
  });

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <div className={classes.formScroll}>
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para cancelar a(s) atividade(s):
            </Typography>
          </Grid>
          {selectedRows.map((row, index) => {
            return (
              <Grid container className={classes.bodyContent}>
                {index > 0 && (
                  <div>
                    <Divider className={classes.divider} />
                  </div>
                )}
                <Input
                  name={`activities.[${index}].activityRealId`}
                  value={row?.activityRealId}
                  inputProps={{ type: 'hidden' }}
                  onChange={formik.handleChange}
                />

                <Input
                  name={`activities.[${index}].activityPlanId`}
                  value={row?.activityPlanId}
                  inputProps={{ type: 'hidden' }}
                  onChange={formik.handleChange}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="activity"
                      label="Atividate"
                      variant="outlined"
                      value={row?.activity}
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      name="startPrev"
                      label="Início previsto"
                      inputVariant="outlined"
                      value={row?.startPrev}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      name="endPrev"
                      label="Término previsto"
                      inputVariant="outlined"
                      value={row?.endPrev}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      name="startReplan"
                      label="Início replanejado"
                      inputVariant="outlined"
                      value={row?.startReplan}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      name="endReplan"
                      label="Término replanejado"
                      inputVariant="outlined"
                      value={row?.endReplan}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="equipment"
                      label="Equipamento"
                      variant="outlined"
                      value={row?.equipment}
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      name={`activities.${index}.comment`}
                      label="Comentário"
                      variant="outlined"
                      value={formik.values.activities[index]?.comment || ''}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: COMMENT_MAX }}
                      helperText={
                        (getIn(
                          formik.touched,
                          `activities.[${index}].comment`,
                        ) &&
                          getIn(
                            formik.errors,
                            `activities.[${index}].comment`,
                          )) ||
                        'Opcional'
                      }
                      error={
                        getIn(
                          formik.touched,
                          `activities.[${index}].comment`,
                        ) &&
                        Boolean(
                          getIn(formik.errors, `activities.[${index}].comment`),
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={`activities.${index}.justification`}
                      label="Justificativa"
                      select
                      variant="outlined"
                      helperText="Obrigatório"
                      value={
                        formik.values.activities[index]?.justification || null
                      }
                      onChange={formik.handleChange}
                      error={
                        getIn(
                          formik.touched,
                          `activities.[${index}].justification`,
                        ) &&
                        Boolean(
                          getIn(
                            formik.errors,
                            `activities.[${index}].justification`,
                          ),
                        )
                      }
                      inputProps={{
                        endAdornment: (
                          <LoadingInputEndAdornment
                            loading={queryJustificationOptions?.isLoading}
                          />
                        ),
                      }}
                    >
                      {justificationOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </form>
      </div>
    );
  }, [
    classes.formScroll,
    classes.formMessage,
    classes.bodyContent,
    classes.divider,
    formik.handleSubmit,
    formik.handleChange,
    formik.values.activities,
    formik.touched,
    formik.errors,
    selectedRows,
    queryJustificationOptions?.isLoading,
    justificationOptions,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Cancelar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="628px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};
export default GridModalCancel;
